import { useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import VoucherManagement from './pages/VoucherManagement';
import StudentManagement from './pages/StudentManagement';
import DistributionManagement from './pages/DistributionManagement';
import Reports from './pages/Reports';
import Dashboard from './pages/Dashboard';
import ExamResults from './pages/ExamResults';
import EmailTemplates from './pages/EmailTemplates';
import ClassManagement from './pages/admin/ClassManagement';
import AdminManagement from './pages/admin/AdminManagement';
import AdminLogin from './pages/admin/AdminLogin';
import AdminMagicVerify from './pages/admin/AdminMagicVerify';
import StudentLogin from './pages/student/StudentLogin';
import StudentPortal from './pages/student/StudentPortal';
import MagicLinkVerify from './pages/student/MagicLinkVerify';
import { Toaster } from '@/components/ui/sonner';
import { FileText, Users, Send, BarChart3, LayoutDashboard, LogOut, GraduationCap, Shield, CheckSquare, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ThemeToggle from '@/components/ThemeToggle';

// Admin protected route wrapper
const ProtectedAdminRoute = ({ children }) => {
  const token = localStorage.getItem('admin_token');
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

const AdminLayout = ({ children }) => {
  const location = useLocation();
  
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    window.location.href = '/admin/login';
  };
  
  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/classes', icon: GraduationCap, label: 'Classes' },
    { path: '/admin/vouchers', icon: FileText, label: 'Vouchers' },
    { path: '/admin/students', icon: Users, label: 'Students' },
    { path: '/admin/distribute', icon: Send, label: 'Distribute' },
    { path: '/admin/exam-results', icon: CheckSquare, label: 'Exam Results' },
    { path: '/admin/email-templates', icon: Mail, label: 'Email Templates' },
    { path: '/admin/reports', icon: BarChart3, label: 'Reports' },
    { path: '/admin/admins', icon: Shield, label: 'Admins' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 black:via-black" >
      <nav className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50 backdrop-blur-sm bg-white/90 dark:bg-slate-800/90" >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100"  style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Voucher Manager
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600 dark:text-slate-400">Admin Portal</span>
              <ThemeToggle />
              <Button 
                onClick={handleLogout} 
                variant="outline"
                className="flex items-center gap-2"
                data-testid="admin-logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          <aside className="w-64 flex-shrink-0">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-4 sticky top-24">
              <nav className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-400 text-white shadow-md'
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 black:hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>
          
          <main className="flex-1">
            {children}
          </main>
        </div>
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

const StudentPortalLayout = ({ children }) => {
  const handleLogout = () => {
    localStorage.removeItem('student_token');
    window.location.href = '/student';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 black:via-black" >
      <nav className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50 backdrop-blur-sm bg-white/90 dark:bg-slate-800/90" >
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-400 rounded-lg flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100"  style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Student Portal
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Button 
                onClick={handleLogout} 
                variant="outline"
                className="flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-6xl mx-auto px-6 py-8">
        {children}
      </div>
      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Redirect root to admin login */}
          <Route path="/" element={<Navigate to="/admin/login" replace />} />
          
          {/* Admin Auth Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/auth/magic" element={<AdminMagicVerify />} />
          
          {/* Admin Routes (Protected) */}
          <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout><Dashboard /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/classes" element={<ProtectedAdminRoute><AdminLayout><ClassManagement /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/vouchers" element={<ProtectedAdminRoute><AdminLayout><VoucherManagement /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/students" element={<ProtectedAdminRoute><AdminLayout><StudentManagement /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/distribute" element={<ProtectedAdminRoute><AdminLayout><DistributionManagement /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/exam-results" element={<ProtectedAdminRoute><AdminLayout><ExamResults /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/email-templates" element={<ProtectedAdminRoute><AdminLayout><EmailTemplates /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/reports" element={<ProtectedAdminRoute><AdminLayout><Reports /></AdminLayout></ProtectedAdminRoute>} />
          <Route path="/admin/admins" element={<ProtectedAdminRoute><AdminLayout><AdminManagement /></AdminLayout></ProtectedAdminRoute>} />
          
          {/* Student Routes */}
          <Route path="/student" element={<StudentLogin />} />
          <Route path="/student/portal" element={<StudentPortalLayout><StudentPortal /></StudentPortalLayout>} />
          <Route path="/student/auth/magic" element={<MagicLinkVerify />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;